<!-- QuickReply.vue -->
<template>
  <div class="reply-panel-container">
    <!-- Tab 切换 -->
    <div class="reply-panel-tabs">
      <div class="reply-panel-tab" :class="{ active: activeTab === 'quick' }" @click="activeTab = 'quick'">快捷回复</div>
      <div class="reply-panel-tab" :class="{ active: activeTab === 'auto' }" @click="activeTab = 'auto'">自动回复</div>
      <div class="reply-panel-tab" :class="{ active: activeTab === 'followup' }" @click="activeTab = 'followup'">超时跟进</div>
    </div>
    <!-- 快捷回复面板 -->
    <div v-show="activeTab === 'quick'" class="quick-reply-container">
    <!-- 工具栏：计数 + 搜索 + 新增（标题由顶部 Tab 承载，避免同名重复） -->
    <div class="quick-reply-header">
      <span class="quick-reply-count">{{ replyCountText }}</span>
      <div class="quick-reply-actions">
        <el-input
          v-model="searchKeyword"
          size="small"
          placeholder="搜索快捷回复"
          prefix-icon="el-icon-search"
          class="quick-reply-search"
          clearable
        ></el-input>
        <el-button
          icon="el-icon-plus"
          size="small"
          circle
          title="新增快捷回复"
          @click="showAddQuickReplyDialog = true"
        ></el-button>
      </div>
    </div>
    
    <!-- 快捷回复列表 -->
    <el-scrollbar class="quick-reply-scroll">
      <div class="quick-reply-list">
        <div
          v-for="reply in filteredQuickReplies"
          :key="reply.id"
          class="quick-reply-item"
          @click="useQuickReply(reply)"
        >
          <!-- 类型徽标 -->
          <div class="quick-reply-badge" :class="'badge-' + reply.type">
            <i :class="getTypeIcon(reply.type)"></i>
          </div>
          <div class="quick-reply-main">
          <!-- 快捷回复名称 -->
          <div class="quick-reply-name">{{ getReplyName(reply) }}</div>
          <!-- 文本消息类型 -->
          <div v-if="reply.type === 'text'" class="quick-reply-content text-type">
            <span>{{ reply.content }}</span>
          </div>
          
          <!-- 图片消息类型 -->
          <div v-else-if="reply.type === 'image'" class="quick-reply-content image-type">
            <div class="image-preview">
              <img :src="reply.content" alt="图片" @error="handleImageError">
              <span class="image-label">图片</span>
            </div>
          </div>
          
          <!-- 图文消息类型 -->
          <div v-else-if="reply.type === 'rich_text'" class="quick-reply-content rich-text-type">
            <div class="rich-text-preview">
              <div class="rich-text-image">
                <img :src="reply.imageUrl" alt="图文封面" @error="handleImageError">
              </div>
              <div class="rich-text-info">
                <div class="rich-text-title">{{ reply.title || '图文消息' }}</div>
                <div class="rich-text-desc">{{ reply.description || '点击查看详情' }}</div>
              </div>
            </div>
          </div>
          
          <!-- 语音消息类型 -->
          <div v-else-if="reply.type === 'voice'" class="quick-reply-content voice-type">
            <span class="voice-duration-text">[语音] {{ getVoiceDuration(reply) }}″</span>
          </div>

          <!-- 视频消息类型 -->
          <div v-else-if="reply.type === 'video'" class="quick-reply-content video-type">
            <div class="video-preview-mini">
              <img v-if="getVideoInfo(reply) && getVideoInfo(reply).snapshotUrl" :src="getVideoInfo(reply).snapshotUrl" alt="视频封面" @error="handleImageError">
              <div v-else class="video-no-cover-mini"><i class="el-icon-video-camera"></i></div>
              <span v-if="getVideoInfo(reply) && getVideoInfo(reply).duration > 0" class="video-duration-mini">{{ formatVideoDuration(getVideoInfo(reply).duration) }}</span>
            </div>
            <span class="video-label">[视频]</span>
          </div>

          <!-- 小程序消息类型（收藏式：素材来源于消息右键"设为快捷回复"） -->
          <div v-else-if="reply.type === 'micro_program'" class="quick-reply-content micro-type">
            <div class="micro-preview">
              <img v-if="getMicroIcon(reply)" :src="getMicroIcon(reply)" class="micro-preview-icon" @error="handleMicroIconError">
              <div class="micro-preview-info">
                <div class="micro-preview-title">{{ getMicroInfo(reply).title || '小程序' }}</div>
                <div class="micro-preview-app">{{ getMicroInfo(reply).appName || '' }}</div>
              </div>
            </div>
          </div>
          </div>
          <!-- 编辑按钮 -->
          <div class="quick-reply-actions">
            <el-button
              icon="el-icon-edit"
              circle
              size="mini"
              title="编辑"
              @click.stop="editQuickReply(reply)"
            ></el-button>
            <!-- 删除按钮 -->
            <el-button
              icon="el-icon-delete"
              circle
              size="mini"
              title="删除"
              @click.stop="deleteQuickReply(reply.id)"
            ></el-button>
          </div>
        </div>
        <!-- 空状态：区分「搜索无匹配」与「无数据」，避免搜索时仍提示点击添加 -->
        <div v-if="filteredQuickReplies.length === 0" class="empty-state">
          <i :class="searchKeyword ? 'el-icon-search' : 'el-icon-chat-line-round'"></i>
          <p>{{ searchKeyword ? '未找到匹配的快捷回复' : '暂无快捷回复' }}</p>
          <el-button v-if="searchKeyword" type="text" @click="searchKeyword = ''">
            清空搜索
          </el-button>
          <el-button v-else type="text" @click="showAddQuickReplyDialog = true">
            点击添加
          </el-button>
        </div>
        <!-- 有数据时给出可点击提示（同时为数据较少的面板收底） -->
        <div v-else class="list-footer-tip">点击卡片即可发送</div>
      </div>
    </el-scrollbar>
    
    <!-- 添加快捷回复对话框 -->
    <el-dialog
      :visible.sync="showAddQuickReplyDialog"
      width="500px"
      :before-close="handleCloseQuickReplyDialog"
    >
      <div slot="title" class="quick-reply-dialog-title">
        <i :class="isEditing ? 'el-icon-edit-outline' : 'el-icon-circle-plus-outline'"></i>
        <span>{{ isEditing ? '编辑快捷回复' : '添加快捷回复' }}</span>
      </div>
      <el-form :model="quickReplyForm" :rules="quickReplyRules" ref="quickReplyForm" label-width="80px">
        <!-- 快捷回复名称 -->
        <el-form-item label="名称" prop="name" key="reply-name">
          <el-input
            v-model="quickReplyForm.name"
            placeholder="请输入快捷回复名称"
            maxlength="32"
            show-word-limit
            prefix-icon="el-icon-collection-tag"
          ></el-input>
        </el-form-item>
        
        <!-- 消息类型选择（图标卡片；小程序素材来源于聊天记录收藏，仅编辑现有小程序快捷回复时展示） -->
        <el-form-item label="消息类型" prop="type">
          <div class="type-card-group" :class="{ 'is-disabled': videoUploading || saving }">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="type-card"
              :class="{ active: quickReplyForm.type === opt.value }"
              @click="selectType(opt.value)"
            >
              <i :class="opt.icon"></i>
              <span>{{ opt.label }}</span>
            </div>
          </div>
        </el-form-item>

        <!-- 小程序消息内容（收藏式，只读展示；素材原样复用发送） -->
        <el-form-item
          v-if="quickReplyForm.type === 'micro_program'"
          label="小程序"
          key="micro-content"
        >
          <div class="micro-preview micro-preview-card">
            <img v-if="microPreviewIcon" :src="microPreviewIcon" class="micro-preview-icon" @error="handleMicroIconError">
            <div class="micro-preview-info">
              <div class="micro-preview-title">{{ microPreviewInfo.title || '小程序' }}</div>
              <div class="micro-preview-app">{{ microPreviewInfo.appName || '' }}</div>
            </div>
          </div>
        </el-form-item>
        
        <!-- 文本消息内容 -->
        <el-form-item 
          v-if="quickReplyForm.type === 'text'" 
          label="回复内容" 
          prop="content"
          key="text-content"
        >
          <el-input
            type="textarea"
            v-model="quickReplyForm.content"
            placeholder="请输入快捷回复内容"
            maxlength="200"
            show-word-limit
            :rows="4"
          ></el-input>
        </el-form-item>
        
        <!-- 图片消息内容 -->
        <el-form-item 
          v-if="quickReplyForm.type === 'image'" 
          label="图片" 
          prop="content"
          key="image-content"
        >
          <div class="image-upload-container">
            <el-upload
                class="avatar-uploader"
                action="action"
                :http-request="customUpload"
                :show-file-list="false"
                :before-upload="beforeImageUpload"
                :auto-upload="true"
            >
              <img v-if="quickReplyForm.content" :src="quickReplyForm.content" class="avatar">
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
            <div class="upload-tips">点击上传图片，支持 JPG、PNG 格式</div>
          </div>
        </el-form-item>
        
        <!-- 图文消息内容 -->
        <template v-if="quickReplyForm.type === 'rich_text'">
          <el-form-item label="封面图片" prop="imageUrl" key="rich-text-image">
            <div class="image-upload-container">
              <el-upload
                class="avatar-uploader"
                action="action"
                :show-file-list="false"
                :http-request="customUpload2"
                :before-upload="beforeImageUpload"
                :auto-upload="true"
              >
                <img v-if="quickReplyForm.imageUrl" :src="quickReplyForm.imageUrl" class="avatar">
                <i v-else class="el-icon-plus avatar-uploader-icon"></i>
              </el-upload>
              <div class="upload-tips">建议尺寸：800×400像素，支持 JPG、PNG 格式</div>
            </div>
          </el-form-item>
          
          <el-form-item label="标题" prop="title" key="rich-text-title">
            <el-input
              v-model="quickReplyForm.title"
              placeholder="请输入图文消息标题"
              maxlength="50"
              show-word-limit
            ></el-input>
          </el-form-item>
          
          <el-form-item label="描述" prop="description" key="rich-text-desc">
            <el-input
              type="textarea"
              v-model="quickReplyForm.description"
              placeholder="请输入图文消息描述"
              maxlength="100"
              show-word-limit
              :rows="3"
            ></el-input>
          </el-form-item>
          
          <el-form-item label="跳转链接" prop="content" key="rich-text-url">
            <el-input
              v-model="quickReplyForm.content"
              placeholder="请输入跳转链接地址"
              type="url"
            >
              <template slot="prepend">http://</template>
            </el-input>
          </el-form-item>
        </template>
        
        <!-- 语音消息内容 -->
        <el-form-item 
          v-if="quickReplyForm.type === 'voice'" 
          label="语音" 
          prop="content"
          key="voice-content"
        >
          <div class="voice-record-container">
            <voice-record-control
              ref="voiceRecordControl"
              :active="showAddQuickReplyDialog && quickReplyForm.type === 'voice'"
              :initial-duration="quickReplyForm.duration"
              @send="onVoiceRecorded"
            ></voice-record-control>
          </div>
        </el-form-item>
        
        <!-- 视频消息内容（与视频消息发送链路同构：本地截帧+上传视频 → VideoElem JSON 存入 content） -->
        <el-form-item 
          v-if="quickReplyForm.type === 'video'" 
          label="视频" 
          prop="content"
          key="video-content"
        >
          <div class="video-upload-container">
            <div v-if="formVideoInfo" class="video-preview">
              <div class="video-cover">
                <img v-if="formVideoInfo.snapshotUrl" :src="formVideoInfo.snapshotUrl" alt="视频封面" @error="handleImageError">
                <div v-else class="video-no-cover"><i class="el-icon-video-camera"></i></div>
                <span v-if="formVideoInfo.duration > 0" class="video-duration-badge">{{ formatVideoDuration(formVideoInfo.duration) }}</span>
              </div>
              <div class="video-meta">
                <div class="video-name" :title="formVideoInfo.name || ''">{{ formVideoInfo.name || '视频' }}</div>
                <div class="video-size">{{ formatVideoSize(formVideoInfo.size) }}</div>
              </div>
            </div>
            <el-upload
              class="video-uploader"
              action="action"
              accept="video/*"
              :show-file-list="false"
              :http-request="customUploadVideo"
              :before-upload="beforeVideoUpload"
              :auto-upload="true"
              :disabled="videoUploading || saving"
            >
              <el-button size="small" type="primary" plain :loading="videoUploading" icon="el-icon-upload2">
                {{ formVideoInfo ? '重新选择' : '选择视频' }}
              </el-button>
            </el-upload>
            <div class="upload-tips">支持常见视频格式，大小不超过 25MB</div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCancelQuickReplyDialog">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="saveQuickReply">确 定</el-button>
      </span>
    </el-dialog>
    </div>
    <!-- 自动回复面板 -->
    <div v-show="activeTab === 'auto'" class="auto-reply-panel-wrapper">
      <auto-reply-panel></auto-reply-panel>
    </div>
    <!-- 超时跟进面板（粉丝超时未回复自动跟进设置） -->
    <div v-show="activeTab === 'followup'" class="auto-reply-panel-wrapper">
      <follow-up-panel></follow-up-panel>
    </div>
  </div>
</template>


<script>
import { addQuickReply, updateQuickReply, deleteQuickReply as deleteQuickReplyApi } from "../../api/quickReply.js";
import { uploadImage, uploadFile } from "../../api/upload.js";
import { readVideoMeta, formatVideoDuration, isVideoFile } from "../../utils/video.js";
import AutoReplyPanel from './AutoReplyPanel.vue'
import FollowUpPanel from './FollowUpPanel.vue'
import VoiceRecordControl from './VoiceRecordControl.vue'

export default {
    name: "QuickReply",
    components: {
      AutoReplyPanel,
      FollowUpPanel,
      VoiceRecordControl
    },
    data() {
        return {
        activeTab: 'quick',
        showAddQuickReplyDialog: false,
        isEditing: false,
        editingId: null,
        searchKeyword: "",
        videoUploading: false, // 视频截帧/上传中（拦截关闭/提交/类型切换，与语音 sending 同口径）
        saving: false, // 保存请求进行中（拦截重选素材/关闭/切换类型，防上传回调写入已重置表单）
        quickReplyForm: {
            name: "", // 快捷回复名称
            type: "text", // 默认文本类型
            content: "", // 消息内容 / 跳转地址 / 语音数据 JSON
            title: "", // 图文消息标题
            description: "", // 图文消息描述
            imageUrl: "", // 图文消息封面图
            duration: 0 // 语音时长（秒）
        },
        quickReplyRules: {
            name: [
            { required: true, message: '请输入快捷回复名称', trigger: 'blur' },
            { min: 1, max: 32, message: '名称长度在 1 到 32 个字符', trigger: 'blur' }
            ],
            type: [
            { required: true, message: '请选择消息类型', trigger: 'change' }
            ],
            content: [
            { required: true, message: '请输入内容', trigger: 'blur' }
            ],
            // 图文消息的验证规则
            title: [
            { required: true, message: '请输入标题', trigger: 'blur' },
            { min: 1, max: 15, message: '标题长度在 1 到 15 个字符', trigger: 'blur' }
            ],
            description: [
            { required: true, message: '请输入描述', trigger: 'blur' },
            { min: 1, max: 50, message: '描述长度在 1 到 50 个字符', trigger: 'blur' }
            ],
            imageUrl: [
            { required: true, message: '请上传封面图片', trigger: 'change' }
            ]
        }
        }
    },
    computed: {
        // 从store获取快捷回复列表
        quickReplies() {
            return this.chatStore.quickReplies;
        },
        // 表单内视频素材预览（content 存 VideoElem JSON）
        formVideoInfo() {
            if (this.quickReplyForm.type !== 'video' || !this.quickReplyForm.content) {
                return null;
            }
            try {
                const data = JSON.parse(this.quickReplyForm.content);
                return data && data.path ? data : null;
            } catch (e) {
                return null;
            }
        },
        // 消息类型选项（图标卡片；小程序素材来源于聊天记录收藏，仅编辑现有小程序快捷回复时展示）
        typeOptions() {
            const options = [
                { value: 'text', label: '文本', icon: 'el-icon-document' },
                { value: 'image', label: '图片', icon: 'el-icon-picture' },
                { value: 'rich_text', label: '图文', icon: 'el-icon-picture-outline' },
                { value: 'voice', label: '语音', icon: 'el-icon-microphone' },
                { value: 'video', label: '视频', icon: 'el-icon-video-camera' }
            ];
            if (this.quickReplyForm.type === 'micro_program') {
                options.push({ value: 'micro_program', label: '小程序', icon: 'el-icon-cpu' });
            }
            return options;
        },
        // 过滤后的快捷回复列表
        filteredQuickReplies() {
            if (!this.searchKeyword) {
                return this.quickReplies;
            }
            return this.quickReplies.filter(reply => {
                const searchContent = (reply.name || '') + ' ' + (reply.type === 'rich_text' 
                ? (reply.title + ' ' + reply.description + ' ' + reply.content)
                : reply.type === 'voice'
                ? ('语音 ' + this.getVoiceDuration(reply) + ' 秒')
                : reply.type === 'video'
                ? this.getVideoSearchText(reply)
                : reply.type === 'micro_program'
                ? ('小程序 ' + (this.getMicroInfo(reply).title || '') + ' ' + (this.getMicroInfo(reply).appName || ''))
                : reply.content);
                return searchContent.toLowerCase().includes(this.searchKeyword.toLowerCase());
            });
        },
        // 计数文案：搜索态展示匹配数，避免与总数混淆
        replyCountText() {
            const total = this.filteredQuickReplies.length;
            return this.searchKeyword ? `找到 ${total} 条` : `共 ${total} 条`;
        },
        // 小程序素材预览（编辑弹窗只读展示）
        microPreviewInfo() {
            try {
                return JSON.parse(this.quickReplyForm.content || '{}');
            } catch (e) {
                return {};
            }
        },
        microPreviewIcon() {
            // 历史数据 weappIconUrl 可能带字面 \u0026，展示前清洗
            return (this.microPreviewInfo.weappIconUrl || '').replace(/\\u0026/g, '&');
        }
    },
    methods: {
        // 视频时长格式化（utils/video.js，与聊天区视频消息展示口径一致）
        formatVideoDuration,
        // 使用快捷回复
        useQuickReply(reply) {
            if (this.chatStore.activeChat) {
                // 触发事件，将快捷回复内容发送到聊天框
                this.$eventBus.$emit('quick-reply-selected', reply);
            } else {
                this.$message.warning('请先选择聊天会话');
            }
        },
        // 保存快捷回复
        saveQuickReply() {
            // 语音转码/上传中禁止提交，防止表单快照旧值导致新录音静默丢失（与关闭拦截一致）
            if (this.$refs.voiceRecordControl && this.$refs.voiceRecordControl.sending) {
                this.$message.warning('语音正在转码上传，请稍候');
                return;
            }
            // 视频截帧/上传中禁止提交（防止表单快照旧值导致新视频静默丢失）；保存请求进行中禁止重复提交
            if (this.videoUploading || this.saving) {
                this.$message.warning(this.saving ? '正在保存，请稍候' : '视频正在上传，请稍候');
                return;
            }
            // 视频类型必须已完成素材上传（content 为有效 VideoElem JSON）
            if (this.quickReplyForm.type === 'video' && !this.formVideoInfo) {
                this.$message.warning('请先选择并上传视频');
                return;
            }
            this.$refs.quickReplyForm.validate((valid) => {
                if (valid) {
                    // 请求期间置忙：类型选择/重选素材/关闭均被互斥拦截，
                    // 防止成功分支 resetForm 后上传回调将旧值写入已重置的表单
                    this.saving = true;
                    // 请求参数
                    const formData = { ...this.quickReplyForm };
                    // 对于图文消息，确保URL格式正确
                    if (formData.type === 'rich_text' && formData.content) {
                        if (!formData.content.startsWith('http://') && !formData.content.startsWith('https://')) {
                        formData.content = 'http://' + formData.content;
                        }
                    }
                    if (this.isEditing) {
                        // 编辑模式
                        updateQuickReply({
                                id: this.editingId,
                                ...formData
                            }).then(() => {
                            this.chatStore.updateQuickReply({
                                id: this.editingId,
                                ...formData
                            });
                            this.$message.success('更新成功');
                            this.showAddQuickReplyDialog = false;
                            this.resetForm();
                        }).catch(() => {
                            this.$message.error('保存失败');
                        }).finally(() => {
                            this.saving = false;
                        });
                    } else {
                        // 新增模式
                        addQuickReply(formData).then((data) => {
                            this.chatStore.addQuickReply(data);
                            this.$message.success('添加快捷回复成功');
                            this.showAddQuickReplyDialog = false;
                            this.resetForm();
                        }).catch(() => {
                            this.$message.error('保存失败');
                        }).finally(() => {
                            this.saving = false;
                        });
                    }
                }
            });
        },
        // 编辑快捷回复
        editQuickReply(reply) {
            this.isEditing = true;
            this.editingId = reply.id;
            // name 归一化为字符串，避免旧数据 null 值被提交到后端
            this.quickReplyForm = { ...reply, name: reply.name || '', duration: this.getVoiceDuration(reply) };
            this.showAddQuickReplyDialog = true;
        },
        // 删除快捷回复
        deleteQuickReply(id) {
            this.$confirm('确定要删除这条快捷回复吗?', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                deleteQuickReplyApi({ id }).then(() => {
                    this.chatStore.deleteQuickReply(id);
                    this.$message.success('删除成功');
                }).catch(() => {
                    this.$message.error('删除失败');
                });
            }).catch(() => {});
        },
        // 选择消息类型（类型卡片点击；重复点击当前类型忽略，保存中拦截切换）
        selectType(value) {
            if (this.quickReplyForm.type === value) {
                return;
            }
            // 保存请求进行中禁止切换（原 radio-group 整体 disabled 的等效拦截）
            if (this.saving) {
                this.$message.warning('正在保存，请稍候');
                return;
            }
            this.handleTypeChange(value);
        },
        // 处理消息类型变化
        handleTypeChange(type) {
            // 语音转码/上传中禁止切换类型，防止语音数据随表单重置而丢失（与关闭拦截一致）
            if (this.$refs.voiceRecordControl && this.$refs.voiceRecordControl.sending) {
                this.$message.warning('语音正在转码上传，请稍候');
                return;
            }
            // 视频截帧/上传中禁止切换类型（同上）
            if (this.videoUploading) {
                this.$message.warning('视频正在上传，请稍候');
                return;
            }
            // 重置表单内容（保留名称，避免编辑时切换类型丢失已填名称）
            this.quickReplyForm = {
                name: this.quickReplyForm.name,
                type: type,
                content: "",
                title: "",
                description: "",
                imageUrl: "",
                duration: 0
            };
        },
        // 语音录制完成回调：语音数据以 VoiceElem JSON 存入 content
        onVoiceRecorded(data) {
            this.quickReplyForm.content = JSON.stringify({
                soundPath: data.url,
                dataSize: data.size || 0,
                duration: data.duration
            });
            this.quickReplyForm.duration = data.duration;
            this.$message.success('语音已保存，点击确定提交');
            // 清除 content 校验错误
            this.$nextTick(() => {
                if (this.$refs.quickReplyForm) {
                    this.$refs.quickReplyForm.clearValidate('content');
                }
            });
        },
        // 视频素材上传（与视频消息发送链路同构）：本地截帧/读时长 → 上传封面+视频 → content 存 VideoElem JSON
        customUploadVideo(options) {
            // 保存请求进行中拒绝新的上传（按钮已禁用，此处兜底防程序化触发）
            if (this.saving) {
                this.$message.warning('正在保存，请稍候');
                return;
            }
            const file = options.file;
            this.videoUploading = true;
            readVideoMeta(file).then((meta) => {
                // 上传封面：截帧/上传失败均降级为空封面，不否决视频保存（与视频消息发送链路同策略）
                let snapshotPromise = Promise.resolve('');
                if (meta.snapshotBlob) {
                    let snapshotFormData = new FormData();
                    snapshotFormData.append('file', meta.snapshotBlob, 'snapshot.jpg');
                    snapshotPromise = uploadImage(snapshotFormData).then((data) => data.path).catch(() => '');
                }
                return snapshotPromise.then((snapshotUrl) => ({ meta, snapshotUrl }));
            }).then(({ meta, snapshotUrl }) => {
                let formData = new FormData();
                formData.append('file', file);
                return uploadFile(formData).then((data) => ({ meta, snapshotUrl, data }));
            }).then(({ meta, snapshotUrl, data }) => {
                // 构造 VideoElem（与后端 msg_inner_struct.go 及视频消息发送链路字段对齐）
                const videoElem = {
                    name: file.name,
                    ext: data.ext,
                    size: file.size,
                    path: data.path,
                    duration: meta.duration,
                    snapshotUrl: snapshotUrl,
                    snapshotWidth: meta.width,
                    snapshotHeight: meta.height
                };
                this.quickReplyForm.content = JSON.stringify(videoElem);
                this.$message.success('视频已上传，点击确定提交');
                // 清除 content 校验错误
                this.$nextTick(() => {
                    if (this.$refs.quickReplyForm) {
                        this.$refs.quickReplyForm.clearValidate('content');
                    }
                });
            }).catch(() => {
                this.$message.error('视频上传失败，请重试');
            }).finally(() => {
                this.videoUploading = false;
            });
        },
        // 视频上传前验证（扩展名判定与视频消息发送链路口径一致；大小上限 25MB）
        beforeVideoUpload(file) {
            if (!isVideoFile(file)) {
                this.$message.error('仅支持视频文件（mp4/mov/avi/mkv/webm/flv/rmvb）');
                return false;
            }
            if (!(file.size < 25 * 1024 * 1024)) {
                this.$message.error('视频大小不能超过 25MB!');
                return false;
            }
            return true;
        },
        // 视频文件大小格式化（表单预览）
        formatVideoSize(size) {
            if (!size) {
                return '0KB';
            }
            if (size >= 1024 * 1024) {
                return (size / 1024 / 1024).toFixed(1) + 'MB';
            }
            return Math.round(size / 1024) + 'KB';
        },
        // 解析语音快捷回复时长（content 为 VoiceElem JSON）
        getVoiceDuration(reply) {
            if (!reply || !reply.content) {
                return 0;
            }
            try {
                const data = JSON.parse(reply.content);
                return data.duration || 0;
            } catch (e) {
                return 0;
            }
        },
        // 解析视频快捷回复素材（content 存 VideoElem JSON）
        getVideoInfo(reply) {
            if (!reply || !reply.content) {
                return null;
            }
            try {
                const data = JSON.parse(reply.content);
                return data && data.path ? data : null;
            } catch (e) {
                return null;
            }
        },
        // 视频快捷回复搜索文本（按文件名/时长匹配，避免 JSON 全文参与匹配）
        getVideoSearchText(reply) {
            const info = this.getVideoInfo(reply);
            if (!info) {
                return '视频';
            }
            return '视频 ' + (info.name || '') + ' ' + this.formatVideoDuration(info.duration);
        },
        // 类型徽标图标映射
        getTypeIcon(type) {
            const iconMap = {
                text: 'el-icon-document',
                image: 'el-icon-picture',
                rich_text: 'el-icon-picture-outline',
                voice: 'el-icon-microphone',
                video: 'el-icon-video-camera',
                micro_program: 'el-icon-cpu'
            };
            return iconMap[type] || 'el-icon-document';
        },
        // 解析小程序快捷回复素材（content 存 MicroProgramElem JSON）
        getMicroInfo(reply) {
            if (!reply || !reply.content) {
                return {};
            }
            try {
                return JSON.parse(reply.content) || {};
            } catch (e) {
                return {};
            }
        },
        // 小程序图标地址（清洗字面 \u0026）
        getMicroIcon(reply) {
            return (this.getMicroInfo(reply).weappIconUrl || '').replace(/\\u0026/g, '&');
        },
        // 小程序图标加载失败回退隐藏
        handleMicroIconError(e) {
            e.target.style.display = 'none';
        },
        // 获取快捷回复名称（旧数据无名称时回退为内容摘要）
        getReplyName(reply) {
            if (reply.name) {
                return reply.name;
            }
            switch (reply.type) {
                case 'rich_text':
                    return reply.title || '图文消息';
                case 'voice':
                    return '[语音] ' + this.getVoiceDuration(reply) + '″';
                case 'video':
                    return '[视频]';
                case 'micro_program':
                    return '[小程序] ' + (this.getMicroInfo(reply).title || '素材');
                case 'image':
                    return '图片';
                default:
                    return this.truncateText(reply.content) || '文本消息';
            }
        },
        // 文本截断为摘要（名称回退展示用）
        truncateText(text, max = 20) {
            if (!text) {
                return '';
            }
            return text.length > max ? text.slice(0, max) + '…' : text;
        },
        // 上传图片快捷回复动作
        customUpload(file) {
            const formData = new FormData();
            formData.append('file', file.file);
            uploadImage(formData).then(res => {
                this.quickReplyForm.content = res.path;
            }).catch(()=> {
                this.$message.error('上传图片错误!');
            })
        },
        // 上传封面
        customUpload2(file) {
            const formData = new FormData();
            formData.append('file', file.file);
            uploadImage(formData).then(res => {
                this.quickReplyForm.imageUrl = res.path;
            }).catch(()=> {
                this.$message.error('上传封面图片错误!');
            })
        },
        // 图片上传前验证
        beforeImageUpload(file) {
            const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isJPG) {
                this.$message.error('上传图片只能是 JPG/PNG/GIF 格式!');
            }
            if (!isLt2M) {
                this.$message.error('上传图片大小不能超过 2MB!');
            }
            return isJPG && isLt2M;
        },
        // 图片加载失败处理
        handleImageError(event) {
            event.target.style.display = 'none';
        },
        // 重置表单
        resetForm() {
            this.quickReplyForm = {
                name: "",
                type: "text",
                content: "",
                title: "",
                description: "",
                imageUrl: "",
                duration: 0
            };
            this.isEditing = false;
            this.editingId = null;
            this.$nextTick(() => {
                if (this.$refs.quickReplyForm) {
                this.$refs.quickReplyForm.clearValidate();
                }
            });
        },
        // 取消按钮：统一走关闭流程，语音转码/上传中同样拦截（与右上角 X 一致）
        handleCancelQuickReplyDialog() {
            this.handleCloseQuickReplyDialog(() => {
                this.showAddQuickReplyDialog = false;
            });
        },
        // 关闭对话框
        handleCloseQuickReplyDialog(done) {
            // 语音转码/上传中禁止关闭，防止误关后语音仍被保存
            if (this.$refs.voiceRecordControl && this.$refs.voiceRecordControl.sending) {
                this.$message.warning('语音正在转码上传，请稍候');
                return;
            }
            // 视频截帧/上传中/保存中禁止关闭（防止误关后上传回调/保存结果写入已重置的表单）
            if (this.videoUploading || this.saving) {
                this.$message.warning(this.saving ? '正在保存，请稍候' : '视频正在上传，请稍候');
                return;
            }
            this.resetForm();
            done();
        }
    }
}
</script>

<style lang="scss" scoped>
.quick-reply-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;

  // 工具栏：计数 + 搜索 + 新增（与左侧会话列表同色系，弱化标题层级）
  .quick-reply-header {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 12px;
    border-bottom: 1px solid #e6e6e6;
    background: #fff;

    .quick-reply-count {
      flex-shrink: 0;
      font-size: 12px;
      color: var(--im-text-color-light);
    }

    .quick-reply-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      flex: 1;

      .quick-reply-search {
        flex: 1;
        max-width: 210px;

        // 全局 .el-input 样式会把圆角撑回默认值，需 deep 修正为胶囊搜索框
        ::v-deep .el-input__inner {
          height: 28px;
          line-height: 28px;
          font-size: 12px;
          border-radius: 14px;
          background: var(--im-background-active);
          border-color: transparent;
          transition: background-color 0.2s ease, border-color 0.2s ease;

          &:focus {
            background: var(--im-background);
            border-color: var(--im-color-primary);
          }
        }
      }

      // 圆形新增按钮：全局 .el-button padding 会撑成椭圆，需强制回正
      ::v-deep .el-button.is-circle {
        width: 28px;
        height: 28px;
        padding: 0 !important;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--im-color-primary);
        border-color: #dcdfe6;

        // 28px 圆内保持图标视觉居中（默认 small 图标 12px 偏小）
        i {
          font-size: 14px;
        }

        &:hover {
          color: #fff;
          background: var(--im-color-primary);
          border-color: var(--im-color-primary);
        }
      }
    }
  }

  .quick-reply-scroll {
    flex: 1;

    // 面板内滚动条加深（全局 im.scss/element.scss 对该类使用 !important，此处需同级覆盖才生效）
    ::v-deep .el-scrollbar__thumb {
      background: rgba(144, 147, 153, 0.35) !important;

      &:hover {
        background: rgba(144, 147, 153, 0.55) !important;
      }
    }

    .quick-reply-list {
      padding: 10px 10px 6px;

      // 卡片：对齐左侧会话列表（10px 圆角、无阴影、hover 浅底），去掉常亮彩条
      .quick-reply-item {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        margin-bottom: 6px;
        background: #fff;
        border-radius: 10px;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background: var(--im-background-active);

          .quick-reply-actions {
            opacity: 1;
            pointer-events: auto;
          }
        }

        // 键盘聚焦到卡片内按钮、或触屏无 hover 时同样展示操作入口（否则编辑/删除不可达）
        &:focus-within {
          .quick-reply-actions {
            opacity: 1;
            pointer-events: auto;
          }
        }

        @media (hover: none) {
          .quick-reply-actions {
            opacity: 1;
            pointer-events: auto;
          }
        }

        .quick-reply-badge {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;
          flex-shrink: 0;

          i {
            font-size: 16px;
          }

          &.badge-text {
            background: #eef0fd;
            i {
              color: var(--im-color-primary);
            }
          }

          &.badge-image {
            background: #f0f9eb;
            i {
              color: #67c23a;
            }
          }

          &.badge-rich_text {
            background: #fdf6ec;
            i {
              color: #e6a23c;
            }
          }

          &.badge-voice {
            background: #f1effd;
            i {
              color: #7d6ef7;
            }
          }

          &.badge-video {
            background: #e8fffb;
            i {
              color: #13c2c2;
            }
          }

          // 小程序徽标此前缺失样式，导致无底色的空圆
          &.badge-micro_program {
            background: #eef2ff;
            i {
              color: #2f54eb;
            }
          }
        }

        .quick-reply-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .quick-reply-name {
          font-weight: 500;
          font-size: 14px;
          line-height: 20px;
          color: #303133;
          margin-bottom: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .quick-reply-content {
          display: flex;
          align-items: center;
          font-size: 12px;
          line-height: 1.5;
          color: var(--im-text-color-light);
          word-break: break-word;
          
          i {
            margin-right: 8px;
            color: var(--im-color-primary);
            font-size: 16px;
          }
          
          // 文本类型样式
          &.text-type {
            span {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          }
          
          // 图片类型样式
          &.image-type {
            .image-preview {
              display: flex;
              align-items: center;
              
              img {
                width: 40px;
                height: 40px;
                object-fit: cover;
                border-radius: 4px;
                margin-right: 8px;
              }
              
              .image-label {
                color: #666;
                font-size: 12px;
              }
            }
          }
          
          // 语音类型样式
          &.voice-type {
            .voice-duration-text {
              color: #666;
              font-size: 12px;
            }
          }
          
          // 视频类型样式
          &.video-type {
            .video-preview-mini {
              position: relative;
              width: 64px;
              height: 40px;
              margin-right: 8px;
              border-radius: 4px;
              overflow: hidden;
              background: #000;
              flex-shrink: 0;

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
                margin: 0;
              }

              .video-no-cover-mini {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;

                i {
                  font-size: 18px;
                  margin: 0;
                  color: #fff;
                }
              }

              .video-duration-mini {
                position: absolute;
                right: 3px;
                bottom: 3px;
                padding: 0 4px;
                border-radius: 2px;
                background: rgba(0, 0, 0, 0.6);
                color: #fff;
                font-size: 10px;
                line-height: 14px;
              }
            }

            .video-label {
              color: #666;
              font-size: 12px;
            }
          }
          
          // 图文消息类型样式
          &.rich-text-type {
            .rich-text-preview {
              display: flex;
              align-items: center;
              // width: 100%;
              
              .rich-text-image {
                width: 40px;
                height: 40px;
                margin-right: 15px;
                flex-shrink: 0;
                
                img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  border-radius: 4px;
                }
              }

              .rich-text-info {
                flex: 1;
                min-width: 0;
                
                .rich-text-title {
                  font-weight: 500;
                  font-size: 13px;
                  margin-bottom: 4px;
                  color: #333;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }
                
                .rich-text-desc {
                  font-size: 12px;
                  color: #666;
                  line-height: 1.3;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
              }
            }
          }
        }
        
        // 操作按钮：默认不可见且不可点击（消除隐形热区误触），hover 卡片才出现
        .quick-reply-actions {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;

          ::v-deep .el-button {
            width: 24px;
            height: 24px;
            margin-left: 0;
            padding: 0 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            background: transparent;
            color: var(--im-text-color-light);

            &:hover {
              background: rgba(0, 0, 0, 0.05);
              color: var(--im-color-primary);
            }
          }

          // 删除按钮 hover 用危险色区分
          ::v-deep .el-button:last-child:hover {
            color: #f56c6c;
            background: rgba(245, 108, 108, 0.1);
          }
        }
      }

      .list-footer-tip {
        padding: 6px 0 4px;
        text-align: center;
        font-size: 12px;
        color: var(--im-text-color-lighter);
        user-select: none;
      }

      .empty-state {
        margin-top: 40px;
        padding: 32px 20px;
        text-align: center;
        background: var(--im-background);
        border-radius: 10px;
        color: var(--im-text-color-light);

        i {
          display: block;
          margin-bottom: 10px;
          font-size: 36px;
          color: var(--im-text-color-lighter);
        }

        p {
          margin: 0 0 8px;
          font-size: 13px;
        }

        // 全局 .el-button padding 会把文本按钮撑成块状，这里收敛为文字链
        ::v-deep .el-button--text {
          padding: 2px 6px !important;
          font-size: 13px;
          color: var(--im-color-primary);

          &:hover {
            color: var(--im-color-primary-light-3);
          }
        }
      }
    }
  }
}

// 语音上传容器样式
.voice-record-container {
  width: 100%;
}

// 视频上传容器样式（表单内：封面预览 + 选择/重新选择）
.video-upload-container {
  width: 100%;

  .video-preview {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .video-cover {
      position: relative;
      width: 160px;
      height: 90px;
      border-radius: 6px;
      overflow: hidden;
      background: #000;
      flex-shrink: 0;
      margin-right: 12px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .video-no-cover {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 28px;
      }

      .video-duration-badge {
        position: absolute;
        right: 4px;
        bottom: 4px;
        padding: 0 5px;
        border-radius: 3px;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        font-size: 11px;
        line-height: 16px;
      }
    }

    .video-meta {
      flex: 1;
      min-width: 0;

      .video-name {
        font-size: 13px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-bottom: 4px;
      }

      .video-size {
        font-size: 12px;
        color: #999;
      }
    }
  }

  .upload-tips {
    margin-top: 8px;
    font-size: 12px;
    color: #666;
  }
}

// 图片上传样式
.image-upload-container {
  .avatar-uploader {
    ::v-deep .el-upload {
      border: 1px dashed #d9d9d9;
      border-radius: 8px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      
      &:hover {
        border-color: #409EFF;
        box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.08);
      }
    }
    
    .avatar-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 178px;
      height: 178px;
      line-height: 178px;
      text-align: center;
    }
    
    .avatar {
      width: 178px;
      height: 178px;
      display: block;
      object-fit: cover;
      border-radius: 8px;
    }
  }
  
  .upload-tips {
    margin-top: 8px;
    font-size: 12px;
    color: #666;
  }
}

// 消息类型选择卡片组（弹窗）
.type-card-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &.is-disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}

.type-card {
  width: 64px;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  i {
    font-size: 18px;
    color: #909399;
    transition: color 0.2s ease;
  }

  span {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1;
    color: #606266;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  &:hover {
    border-color: #b3d8ff;
    background: #f5faff;

    i {
      color: #409EFF;
    }
  }

  &.active {
    border-color: #409EFF;
    background: #ecf5ff;

    i,
    span {
      color: #409EFF;
    }

    span {
      font-weight: 500;
    }
  }
}

// 对话框表单样式
::v-deep .el-dialog {
  border-radius: 8px;
  
  .el-form-item {
    margin-bottom: 20px;
    
    .el-form-item__label {
      font-weight: 500;
      color: #333;
    }
    
    .el-textarea {
      .el-textarea__inner {
        resize: vertical;
        min-height: 80px;
      }
    }
    
    .el-input {
      .el-input-group__prepend {
        background-color: #f5f7fa;
        color: #909399;
      }
    }
  }
  
  .dialog-footer {
    .el-button {
      min-width: 80px;

      // 主按钮统一品牌色（原硬编码 #409EFF 与主题 #2830d3 不一致）
      &.el-button--primary {
        background: var(--im-color-primary);
        border-color: var(--im-color-primary);

        &:hover {
          background: var(--im-color-primary-light-3);
          border-color: var(--im-color-primary-light-3);
        }
      }
    }
  }
}

// 侧栏为固定 420px 的 PC 面板，不再保留 ≤768px 的响应式覆盖（会与卡片内边距/滚动区分隔冲突）

// 滚动条自定义样式
::v-deep .el-scrollbar {
  height: 100%;
  
  .el-scrollbar__wrap {
    overflow-x: hidden;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
      
      &:hover {
        background: #a8a8a8;
      }
    }
  }
  
  .el-scrollbar__bar {
    &.is-vertical {
      width: 6px;
    }
    
    .el-scrollbar__thumb {
      background-color: #c1c1c1;
      
      &:hover {
        background-color: #a8a8a8;
      }
    }
  }
}

// 列表项不再使用进入动画：会话切换/新增后频繁重渲染会导致卡片反复上移淡入，干扰阅读

// 加载状态
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  
  .el-icon-loading {
    font-size: 24px;
    margin-right: 8px;
  }
}

// 错误状态
.error-state {
  text-align: center;
  padding: 40px 20px;
  color: #f56c6c;
  
  i {
    font-size: 40px;
    margin-bottom: 10px;
    display: block;
  }
  
  p {
    margin-bottom: 10px;
    font-size: 14px;
  }
  
  .el-button {
    color: #f56c6c;
    
    &:hover {
      color: #f78989;
    }
  }
}

// selected/disabled 状态样式已移除：模板未使用这两个状态类，且原样式依赖已去掉的左边框/阴影

// 文字计数样式
::v-deep .el-input {
  .el-input__count {
    background: transparent;
    color: #909399;
    
    .el-input__count-inner {
      font-size: 12px;
    }
  }
}

// 表单验证样式
::v-deep .el-form-item {
  &.is-error {
    .el-input__inner,
    .el-textarea__inner {
      border-color: #f56c6c;
    }
    
    .el-form-item__error {
      color: #f56c6c;
      font-size: 12px;
      padding-top: 4px;
    }
  }
  
  &.is-success {
    .el-input__inner,
    .el-textarea__inner {
      border-color: #67c23a;
    }
  }
}

// 按钮组样式
.quick-reply-actions {
  .el-button-group {
    display: flex;
    
    .el-button {
      margin-left: 0;
      border-radius: 0;
      
      &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
      
      &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }
  }
}

// 图标样式优化
.quick-reply-content {
  i {
    &.el-icon-document {
      color: #409EFF;
    }
    
    &.el-icon-picture {
      color: #67c23a;
    }
    
    &.el-icon-picture-outline {
      color: #e6a23c;
    }
    
    &.el-icon-microphone {
      color: #67c23a;
    }
    
    &.el-icon-link {
      color: #f56c6c;
    }
  }
}

// 小程序素材预览（列表与编辑弹窗只读展示共用）
.micro-preview {
  display: flex;
  align-items: center;

  .micro-preview-icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    margin-right: 8px;
    flex-shrink: 0;
    object-fit: cover;
  }

  .micro-preview-info {
    min-width: 0;

    .micro-preview-title {
      font-size: 13px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .micro-preview-app {
      font-size: 12px;
      color: #999;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

// 弹窗内小程序预览（只读展示卡片）
.micro-preview-card {
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

// 图片预览优化
.image-preview,
.rich-text-image {
  position: relative;
  
  img {
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  .image-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #409EFF;
  }
}

// 空状态样式统一由 .quick-reply-scroll .empty-state 承载（不再做无限脉冲动画，避免持续闪烁干扰）

// 对话框标题样式
::v-deep .el-dialog__header {
  border-bottom: 1px solid #e6e6e6;
  padding: 15px 20px;
  
  .el-dialog__title {
    font-weight: 600;
    color: #333;
  }
  
  // 自定义标题（图标 + 文案）
  .quick-reply-dialog-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    
    i {
      margin-right: 8px;
      font-size: 18px;
      color: #409EFF;
      font-weight: normal;
    }
  }
  
  .el-dialog__headerbtn {
    top: 15px;
    
    .el-dialog__close {
      color: #909399;
      
      &:hover {
        color: #409EFF;
      }
    }
  }
}

// 对话框内容样式
::v-deep .el-dialog__body {
  padding: 20px;
  
  .el-form {
    .el-form-item:last-child {
      margin-bottom: 0;
    }
  }
}

// 对话框底部样式
::v-deep .el-dialog__footer {
  border-top: 1px solid #e6e6e6;
  padding: 15px 20px;
}

// Tab 容器样式
.reply-panel-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  border-left: 1px solid #e6e6e6;

  .reply-panel-tabs {
    display: flex;
    border-bottom: 2px solid #e6e6e6;
    background: #fff;
    flex-shrink: 0;

    .reply-panel-tab {
      flex: 1;
      text-align: center;
      padding: 12px 0;
      cursor: pointer;
      font-size: 14px;
      color: #666;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;
      transition: all 0.2s ease;

      &:hover {
        color: #409EFF;
      }

      &.active {
        color: #409EFF;
        border-bottom-color: #409EFF;
        font-weight: 600;
      }
    }
  }
}

.auto-reply-panel-wrapper {
  flex: 1;
  overflow: hidden;
}
</style>